import { DataSchemaType, Dataset, DatasetSchemaLabel } from "@/types/dataset";
import { tryParseJsonArray } from "@/utils/string";

import { FeaturePhotoItem, FeaturePhotoValueType } from "./types";

export type FeatureLinkItem = {
  url: string;
  label?: string;
};

export type DisplayAttribute = {
  key: string;
  value:
    | string
    | number
    | boolean
    | (string | number | boolean)[]
    | null
    | undefined;
  dataType?: DataSchemaType | DatasetSchemaLabel;
  originalIndex: number;
  photos?: FeaturePhotoItem[];
};

export type GroupedAttributes = {
  primary: DisplayAttribute[];
  secondary: DisplayAttribute[];
};

export const findItemById = (
  obj: {
    [id: string]: Dataset;
  },
  id: string | undefined
): Dataset | null => {
  const KEY = ["-point", "-polygon"];

  if (!id || !obj) return null;

  // Remove any value in KEY from the end of id
  let baseId = id;
  for (const suffix of KEY) {
    if (baseId.endsWith(suffix)) {
      baseId = baseId.slice(0, -suffix.length);
      break;
    }
  }

  return obj[baseId] || null;
};

export const getAttributePriority = (
  dataType?: DataSchemaType | DatasetSchemaLabel
) => {
  if (dataType === DataSchemaType.PHOTOS) return 0;
  if (dataType === DataSchemaType.LINKS) return 1;
  return 2;
};

export const getPhotoTypePriority = (type: FeaturePhotoValueType): number => {
  switch (type) {
    case FeaturePhotoValueType.ARRAY_OBJECT:
      return 0;
    case FeaturePhotoValueType.ARRAY_VALUE:
      return 1;
    case FeaturePhotoValueType.SINGLE:
    default:
      return 2;
  }
};

/**
 * Determines if an attribute should be displayed in the primary section
 */
const isPrimaryAttribute = (
  dataType?: DataSchemaType | DatasetSchemaLabel
): boolean => {
  return (
    dataType === DataSchemaType.PHOTOS ||
    dataType === DataSchemaType.ADDRESS ||
    dataType === DataSchemaType.LINKS
  );
};

/**
 * Groups display attributes into primary and secondary sections
 */
export const groupAttributesByPriority = (
  displayAttributes: DisplayAttribute[],
  adminAreaLabelKeys: Set<string>
): GroupedAttributes => {
  return displayAttributes.reduce<GroupedAttributes>(
    (groups, attribute) => {
      const { dataType, key } = attribute;

      if (isPrimaryAttribute(dataType)) {
        groups.primary.push(attribute);
      } else if (!adminAreaLabelKeys.has(key)) {
        groups.secondary.push(attribute);
      }

      return groups;
    },
    { primary: [], secondary: [] }
  );
};

/**
 * Determines the display order priority for primary attributes
 */
export const getPrimaryAttributeOrder = (
  attribute: DisplayAttribute
): number => {
  const { dataType, key } = attribute;

  if (dataType === DataSchemaType.PHOTOS) return 0;
  if (dataType === DataSchemaType.ADDRESS) return 1;
  if (key === "adm_area") return 2;
  if (dataType === DataSchemaType.LINKS) return 3;
  return 4;
};

/**
 * Sorts primary attributes by type order and original position
 */
export const sortPrimaryAttributes = (
  attributes: DisplayAttribute[]
): DisplayAttribute[] => {
  const sorted = [...attributes];
  sorted.sort((a, b) => {
    const typeOrderDiff =
      getPrimaryAttributeOrder(a) - getPrimaryAttributeOrder(b);
    if (typeOrderDiff !== 0) return typeOrderDiff;
    return a.originalIndex - b.originalIndex;
  });
  return sorted;
};

/**
 * Builds a virtual admin area attribute from individual admin area keys
 */
export const buildAdminAreaAttribute = (
  adminAreaKeys: string[],
  attributeEntriesByNormalizedKey: Map<
    string,
    { key: string; value: unknown; index: number }
  >,
  normalizeKey: (key: string) => string,
  resultTransform: Record<string, unknown>
): DisplayAttribute | null => {
  let adminAreaReferenceIndex = Number.MAX_SAFE_INTEGER;

  const adminAreaValues = adminAreaKeys
    .map((rawKey) => {
      const entry = attributeEntriesByNormalizedKey.get(normalizeKey(rawKey));
      if (!entry) return null;

      const { key: originalKey, value, index } = entry;

      const transformedValue = Object.prototype.hasOwnProperty.call(
        resultTransform,
        originalKey
      )
        ? resultTransform[originalKey]
        : value;

      if (transformedValue === null || transformedValue === undefined) {
        return null;
      }

      if (index < adminAreaReferenceIndex) {
        adminAreaReferenceIndex = index;
      }

      return transformedValue;
    })
    .filter((value): value is string => typeof value === "string");

  if (adminAreaValues.length === 0) {
    return null;
  }

  return {
    key: "adm_area",
    value: adminAreaValues,
    dataType: undefined,
    originalIndex: adminAreaReferenceIndex
  };
};

export const isArrayOfObjectsString = (val: unknown): val is string => {
  const parsed = tryParseJsonArray(val);
  return (
    parsed !== null &&
    parsed.length > 0 &&
    typeof parsed[0] === "object" &&
    parsed[0] !== null
  );
};

export const isArrayOfPrimitivesString = (val: unknown): val is string => {
  const parsed = tryParseJsonArray(val);
  return parsed !== null && parsed.length > 0 && typeof parsed[0] !== "object";
};

export const getDisplayValue = (val: unknown): string => {
  if (Array.isArray(val)) return val.join("\n");

  const jsonArray = tryParseJsonArray(val);
  if (jsonArray !== null) {
    if (isArrayOfObjectsString(val)) return "";
    return jsonArray.join("\n");
  }
  return String(val);
};
